import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, Search, X } from "lucide-react";
import Chatbot from "@/components/Chatbot";
import { COMPANY, LOGO_URL } from "@/lib/brand";

const NAV = [
  { to: "/jobs", label: "Jobs" },
  { to: "/sponsorship-jobs", label: "Sponsorship jobs" },
  { to: "/sponsors", label: "Sponsor directory" },
  { to: "/study", label: "Study" },
  { to: "/programmes", label: "Programmes" },
  { to: "/verify", label: "Verification" },
  { to: "/visas", label: "Visas" },
  { to: "/consultations", label: "Consultations" },
];

export const Layout = ({ children }) => {
  const { user, tenant, logout, hasRole } = useAuth();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const crumbs = pathname.split("/").filter(Boolean);
  const isActive = (to) => pathname === to || pathname.startsWith(`${to}/`);

  return (
    <div className="min-h-screen flex flex-col">
      <a href="#main" className="sr-only focus:not-sr-only p-2">Skip to main content</a>
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm shadow-slate-100/60">
        <div className="max-w-7xl mx-auto px-5 h-20 flex items-center gap-4">
          <Link to="/" data-testid="brand-home-link" className="flex items-center gap-2 shrink-0" aria-label={`${COMPANY.name} home`}>
            <img src={LOGO_URL} alt={`${COMPANY.name} logo`} className="h-12 w-auto" data-testid="brand-logo" />
            {tenant && tenant.slug !== "europedu" && (
              <span className="display text-sm font-bold text-[#1e3a8a]">{tenant.logo_text}</span>
            )}
          </Link>

          <nav className="hidden xl:flex items-center gap-4 text-[13px] font-semibold text-slate-600">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.slice(1)}`}
                aria-current={isActive(n.to) ? "page" : undefined}
                className={`whitespace-nowrap transition-colors ${
                  isActive(n.to) ? "text-[#1e3a8a]" : "hover:text-[#1e3a8a]"
                }`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <form
            className="ml-auto hidden 2xl:flex items-center relative"
            onSubmit={(e) => { e.preventDefault(); navigate(`/jobs?q=${encodeURIComponent(q)}`); }}
          >
            <Search className="w-4 h-4 absolute left-3 text-slate-400" />
            <input
              data-testid="global-search-input"
              aria-label="Global search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search jobs, sponsors, programmes"
              className="field pl-9 w-52"
            />
          </form>

          <div className="ml-auto hidden md:flex items-center gap-3 2xl:ml-0">
            {user ? (
              <>
                {hasRole("super_admin", "tenant_admin", "tenant_owner", "compliance_reviewer", "support_staff", "content_editor", "finance_admin") ? (
                  <Link to="/admin" data-testid="nav-dashboard" className="text-sm font-semibold text-[#1e3a8a]">Dashboard</Link>
                ) : (
                  <Link to="/dashboard" data-testid="nav-dashboard" className="text-sm font-semibold text-[#1e3a8a]">Dashboard</Link>
                )}
                <button data-testid="logout-button" onClick={() => { logout(); navigate("/"); }}
                  className="btn btn-ghost py-1.5 px-3 text-sm">Sign out</button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login" className="text-sm font-semibold text-slate-700">Sign in</Link>
                <Link to="/register" data-testid="nav-register" className="btn btn-primary py-2 text-sm">Create account</Link>
              </>
            )}
          </div>

          <button
            className="ml-auto md:ml-0 xl:hidden rounded-lg p-2 hover:bg-slate-100"
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
            aria-expanded={open}
            onClick={() => setOpen(!open)}
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>

        {open && (
          <div className="xl:hidden border-t border-slate-200 px-5 py-5 bg-white" data-testid="mobile-menu">
            <div className="max-w-7xl mx-auto grid gap-3 sm:grid-cols-2">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className={`rounded-lg px-3 py-2 text-sm font-medium ${isActive(n.to) ? "bg-blue-50 text-[#1e3a8a]" : "text-slate-700 hover:bg-slate-50"}`}
                >
                  {n.label}
                </Link>
              ))}
            </div>
            <div className="max-w-7xl mx-auto mt-4 pt-4 border-t border-slate-100 flex flex-wrap gap-3 md:hidden">
              {user ? (
                <Link to={hasRole("super_admin", "tenant_admin", "tenant_owner", "compliance_reviewer", "support_staff", "content_editor", "finance_admin") ? "/admin" : "/dashboard"} onClick={() => setOpen(false)} className="btn btn-primary text-sm">Dashboard</Link>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="btn btn-ghost text-sm">Sign in</Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="btn btn-primary text-sm">Create account</Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      {crumbs.length > 0 && (
        <nav aria-label="Breadcrumb" className="max-w-7xl mx-auto px-5 pt-4 text-xs text-slate-500" data-testid="breadcrumbs">
          <Link to="/" className="hover:underline">Home</Link>
          {crumbs.map((c, i) => (
            <span key={i}> / <span className="capitalize">{decodeURIComponent(c).replace(/-/g, " ")}</span></span>
          ))}
        </nav>
      )}

      <main id="main" className="flex-1">{children}</main>

      <footer className="mt-24 bg-[#0f172a] text-slate-300">
        <div className="max-w-7xl mx-auto px-5 py-14 grid gap-10 md:grid-cols-4">
          <div>
            <img src={LOGO_URL} alt={`${COMPANY.name} logo`} className="h-14 w-auto bg-white rounded-xl p-2" />
            <p className="display text-lg font-bold text-white mt-3">{tenant?.company_name || COMPANY.name}</p>
            <p className="text-sm mt-2 text-slate-400">Careers, education, recruitment and immigration services. Registered in the United Kingdom.</p>
            <p className="text-sm mt-3 text-slate-400">{COMPANY.email}<br />{COMPANY.phones.join(" · ")}</p>
          </div>
          <div>
            <p className="overline text-slate-400">Services</p>
            <ul className="mt-3 space-y-2 text-sm">
              {NAV.slice(0, 5).map((n) => <li key={n.to}><Link to={n.to} className="hover:text-white">{n.label}</Link></li>)}
            </ul>
          </div>
          <div>
            <p className="overline text-slate-400">Platform</p>
            <ul className="mt-3 space-y-2 text-sm">
              <li><Link to="/academy" className="hover:text-white">Academy</Link></li>
              <li><Link to="/partners" className="hover:text-white">Sales partners</Link></li>
              <li><Link to="/white-label" className="hover:text-white">White-label platform</Link></li>
              <li><Link to="/news" className="hover:text-white">Newsroom</Link></li>
              <li><Link to="/cv-builder" className="hover:text-white">CV builder</Link></li>
              <li><Link to="/cv-marketing" className="hover:text-white">CV marketing packages</Link></li>
            </ul>
          </div>
          <div className="text-sm text-slate-400">
            <p className="overline text-slate-400">Legal and trust</p>
            <ul className="mt-3 space-y-2 text-sm">
              {[["privacy-notice", "Privacy Notice"], ["terms-and-conditions", "Terms and Conditions"],
                ["cookie-policy", "Cookie Policy"], ["refund-and-cancellation-policy", "Refund and Cancellation"],
                ["complaints-procedure", "Complaints Procedure"], ["accessibility-statement", "Accessibility Statement"],
                ["ai-use-and-transparency", "AI Use and Transparency"], ["recruitment-disclaimer", "Recruitment Disclaimer"],
                ["sponsorship-disclaimer", "Sponsorship Disclaimer"], ["education-disclaimer", "Education Disclaimer"],
                ["visa-information-disclaimer", "Visa Information Disclaimer"],
                ["document-verification-terms", "Document Verification Terms"],
                ["consultation-terms", "Consultation Terms"], ["partner-terms", "Partner Terms"]].map(([s, l]) => (
                <li key={s}><Link to={`/legal/${s}`} data-testid={`footer-legal-${s}`} className="hover:text-white">{l}</Link></li>
              ))}
            </ul>
            <p className="mt-4 text-xs">
              EuropEdu does not guarantee employment, sponsorship, admission, a Certificate of Sponsorship or a
              visa. Information is general and is not immigration advice unless provided by a regulated adviser
              under a regulated service.
            </p>
          </div>
        </div>
      </footer>
      <Chatbot />
    </div>
  );
};

export default Layout;
